import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TablePagination, TableHead, TableRow, Paper, IconButton, Button, Box, CircularProgress, } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCategoriesByType, updateCategoryAndSubCategories, deleteCategory, addCategory } from '../../services/CategoryService';
import { permissionOptions } from '../../utils/Enums';
import { getIcon } from '../../utils/IconHandler';
import EditCategoryModal from './EditCategoryModal';
import DeleteCategoryModal from './DeleteCategoryModal';
import AddCategoryModal from './AddCategoryModal';
import { getProfile } from '../../services/UserService';

const FRANCHISE_ROLE_CODES = ['FRANCHISE_MANAGER', 'FRANCHISEE_MANAGER'];
const FRANCHISE_ROLE_NAMES = ['Franquiciante', 'Franquiciado'];

const CategoriesTable = ({ categoryType }) => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);

    const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
    const hasPermission = (permissionId) => userPermissions.includes(permissionId);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [isFranchiseRole, setIsFranchiseRole] = useState(false);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                setLoading(true);
                const data = await getCategoriesByType(categoryType);
                if (data) {
                    setCategories(data);
                }
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            } finally {
                setLoading(false);
            }
        };

        loadCategories();
    }, [categoryType]);

    useEffect(() => {
        let isMounted = true;

        getProfile()
            .then((profile) => {
                if (!isMounted) return;
                const role = profile?.role;
                setIsFranchiseRole(
                    FRANCHISE_ROLE_CODES.includes(role?.templateCode) ||
                    FRANCHISE_ROLE_NAMES.includes(role?.name)
                );
            })
            .catch(() => {
                if (isMounted) setIsFranchiseRole(true);
            })
            .finally(() => {
                if (isMounted) setProfileLoaded(true);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleAddClick = () => {
        setAddModalOpen(true);
    };

    const handleEditClick = (category) => {
        setSelectedCategory(category);
        setEditModalOpen(true);
    };
      
    const handleDeleteClick = (category) => {
        setSelectedCategory(category);
        setDeleteModalOpen(true);
    };
      
    const handleCloseAddModal = () => {
        setAddModalOpen(false);
    };
    
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedCategory(null);
    };
      
    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
        setSelectedCategory(null);
    };
    
    const handleCategoryAdded = async (categoryData) => {
        try {
            let request = {
                name: categoryData.name,
                type: categoryType,
                sub_categories: categoryData.subCategories.map(sub => ({
                    name: sub.name
                }))
            };
            const updatedCategories = await addCategory(request);
            setCategories(updatedCategories);
        } catch (error) {
            console.error('Failed to add category:', error);
        }
    };

    const handleCategoryUpdated = async (categoryData) => {
        try {
            let request = {
                id: categoryData.id,
                name: categoryData.name,
                type: categoryData.type,
                sub_categories: categoryData.subCategories.map(sub => ({
                    id: sub.id,
                    name: sub.name,
                    category_id: sub.categoryId
                }))
            };
            const updatedCategories = await updateCategoryAndSubCategories(request);
            setCategories(updatedCategories);
        } catch (error) {
            console.error('Failed to update category:', error);
        }
    };
    
    const handleCategoryDelete = async (categoryId) => {
        const updatedCategories = await deleteCategory(categoryId);
        setCategories(updatedCategories);
    };

    const canCreateCategory =
        profileLoaded &&
        !isFranchiseRole &&
        hasPermission(permissionOptions.find(p => p.label === 'CREATE_CATEGORY').id);

    const canUpdateCategory =
        profileLoaded &&
        !isFranchiseRole &&
        hasPermission(permissionOptions.find(p => p.label === 'UPDATE_CATEGORY').id);

    const canDeleteCategory =
        profileLoaded &&
        !isFranchiseRole &&
        hasPermission(permissionOptions.find(p => p.label === 'DELETE_CATEGORY').id);

    const showActionsColumn =
        canUpdateCategory ||
        canDeleteCategory;

    return (
        <>
            <Paper sx={{ padding: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                    {canCreateCategory && (
                        <Button variant="outlined" startIcon={getIcon('add')} onClick={() => handleAddClick()}>
                            {t('add_category')}
                        </Button>
                    )}
                </Box>
                <TableContainer component={Paper} style={{boxShadow: 'none'}}>
                    <Table stickyHeader aria-label="categories table">
                        <TableHead>
                            <TableRow>
                                <TableCell><span className="roboto-regular">{t('category')}</span></TableCell>
                                <TableCell align="center"><span className="roboto-regular">{t('sub_category')}</span></TableCell>
                                {showActionsColumn && (
                                  <TableCell align="right"><span className="roboto-regular">{t('actions')}</span></TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {loading ? (
                            <TableRow>
                            <TableCell colSpan={6} align="center">
                                <Box py={1}  justifyContent="center"><CircularProgress size={32} /></Box>
                            </TableCell>
                            </TableRow>
                        ) : (
                            categories.map((category) => (
                                <TableRow key={category.id}>
                                    <TableCell><span className="roboto-light">{category.name}</span></TableCell>
                                    <TableCell align="center">
                                    {category.subCategories && category.subCategories.length > 0 ? (
                                        <div>
                                            {category.subCategories.map(sub => (
                                                <li key={sub.id}><span className="roboto-light">{sub.name}</span></li>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="roboto-light" style={{ color: "#b4b4b4" }}>{t('no_subcategories')}</span>
                                    )}
                                    </TableCell>
                                    {showActionsColumn && (
                                        <TableCell align="right">
                                            {canUpdateCategory && (
                                            <IconButton onClick={() => handleEditClick(category)}>{getIcon('settings')}</IconButton>
                                            )}
                                            {canDeleteCategory && (
                                            <IconButton onClick={() => handleDeleteClick(category)}>{getIcon('trash')}</IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={categories.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <AddCategoryModal
                open={addModalOpen}
                onClose={handleCloseAddModal}
                onSave={handleCategoryAdded}
            />

            {selectedCategory && (
                <EditCategoryModal
                    open={editModalOpen}
                    onClose={handleCloseEditModal}
                    category={selectedCategory}
                    onUpdate={handleCategoryUpdated}
                />
            )}
            
            {selectedCategory && (
                <DeleteCategoryModal
                    categoryName={selectedCategory.name}
                    open={deleteModalOpen}
                    onClose={handleCloseDeleteModal}
                    onConfirm={() => handleCategoryDelete(selectedCategory.id)}
                />
            )}
        </>
    );
};

export default CategoriesTable;
